import type { Editor } from '@tiptap/core';
import { useEditorState } from '@tiptap/react';
import type { EmailBlock, TextAlign } from '../HtmlEditor.types';

type InlineFormatBarProps = {
  block: EmailBlock;
  editor: Editor | null;
  onChange?: (patch: Partial<EmailBlock>) => void;
  disabled?: boolean;
};

const VARIABLE_PRESETS = ['{{name}}', '{{EMAIL}}', '{{user}}'];

function toolbarButtonProps(active: boolean) {
  return active ? 'is-on' : '';
}

function preventToolbarMouseDown(event: { preventDefault: () => void }) {
  event.preventDefault();
}

const DEFAULT_INLINE_BAR_STATE = {
  isBold: false,
  canBold: false,
  isItalic: false,
  canItalic: false,
  isUnderline: false,
  canUnderline: false,
  isStrike: false,
  canStrike: false,
  isBulletList: false,
  isOrderedList: false,
};

function inlineBarStateSelector(ctx: { editor: Editor | null }) {
  if (!ctx.editor) {
    return DEFAULT_INLINE_BAR_STATE;
  }

  return {
    isBold: ctx.editor.isActive('bold'),
    canBold: ctx.editor.can().chain().focus().toggleBold().run(),
    isItalic: ctx.editor.isActive('italic'),
    canItalic: ctx.editor.can().chain().focus().toggleItalic().run(),
    isUnderline: ctx.editor.isActive('underline'),
    canUnderline: ctx.editor.can().chain().focus().toggleUnderline().run(),
    isStrike: ctx.editor.isActive('strike'),
    canStrike: ctx.editor.can().chain().focus().toggleStrike().run(),
    isBulletList: ctx.editor.isActive('bulletList'),
    isOrderedList: ctx.editor.isActive('orderedList'),
  };
}

export function InlineFormatBar({ block, editor, onChange, disabled }: InlineFormatBarProps) {
  const font = 'font' in block ? block.font : 'Arial';
  const size = 'size' in block ? block.size : 14;
  const color = 'color' in block ? block.color : '#0F172A';

  const formatState = useEditorState({
    editor,
    selector: inlineBarStateSelector,
  });

  const state = formatState ?? DEFAULT_INLINE_BAR_STATE;

  const run = (command: () => void) => {
    if (disabled || !editor) {
      return;
    }
    command();
  };

  const handleColorChange = (nextColor: string) => {
    onChange?.({ color: nextColor } as Partial<EmailBlock>);

    if (editor && !editor.state.selection.empty) {
      editor.chain().focus().setColor(nextColor).run();
    }
  };

  const runAlign = (align: TextAlign) => {
    onChange?.({ align } as Partial<EmailBlock>);
  };

  return (
    <div className='ee-inlinebar' onClick={(e) => e.stopPropagation()}>
      <select
        className='ee-inlinebar__select'
        value={font}
        style={{ width: 110 }}
        disabled={disabled}
        onChange={(e) => onChange?.({ font: e.target.value } as Partial<EmailBlock>)}
      >
        <option>Arial</option>
        <option>Helvetica</option>
        <option>Georgia</option>
        <option>Verdana</option>
        <option>Times New Roman</option>
      </select>
      <input
        className='ee-inlinebar__num'
        type='number'
        value={size}
        disabled={disabled}
        onChange={(e) => onChange?.({ size: +e.target.value } as Partial<EmailBlock>)}
      />
      <label className='ee-inlinebar__color' style={{ background: color }} title='Text color'>
        <input
          type='color'
          value={color}
          disabled={disabled}
          onChange={(e) => handleColorChange(e.target.value)}
          style={{ opacity: 0, width: '100%', height: '100%', cursor: 'pointer' }}
        />
      </label>
      <span className='ee-inlinebar__sep' />
      <button
        type='button'
        title='Bold'
        className={toolbarButtonProps(state.isBold)}
        style={{ fontWeight: 800 }}
        disabled={disabled || !editor || !state.canBold}
        onMouseDown={preventToolbarMouseDown}
        onClick={() => run(() => editor?.chain().focus().toggleBold().run())}
      >
        B
      </button>
      <button
        type='button'
        title='Italic'
        className={toolbarButtonProps(state.isItalic)}
        style={{ fontStyle: 'italic' }}
        disabled={disabled || !editor || !state.canItalic}
        onMouseDown={preventToolbarMouseDown}
        onClick={() => run(() => editor?.chain().focus().toggleItalic().run())}
      >
        I
      </button>
      <button
        type='button'
        title='Underline'
        className={toolbarButtonProps(state.isUnderline)}
        style={{ textDecoration: 'underline' }}
        disabled={disabled || !editor || !state.canUnderline}
        onMouseDown={preventToolbarMouseDown}
        onClick={() => run(() => editor?.chain().focus().toggleUnderline().run())}
      >
        U
      </button>
      <button
        type='button'
        title='Strike'
        className={toolbarButtonProps(state.isStrike)}
        style={{ textDecoration: 'line-through' }}
        disabled={disabled || !editor || !state.canStrike}
        onMouseDown={preventToolbarMouseDown}
        onClick={() => run(() => editor?.chain().focus().toggleStrike().run())}
      >
        S
      </button>
      <span className='ee-inlinebar__sep' />
      <button
        type='button'
        title='Variable'
        disabled={disabled || !editor}
        onMouseDown={preventToolbarMouseDown}
        onClick={() => {
          const variable = window.prompt('Variable name (e.g. name)', VARIABLE_PRESETS[0]) ?? '';
          if (!variable.trim() || !editor) {
            return;
          }
          const token = variable.includes('{{') ? variable : `{{${variable}}}`;
          editor.chain().focus().insertContent(token).run();
        }}
      >
        {'{ }'}
      </button>
      <span className='ee-inlinebar__sep' />
      <button
        type='button'
        title='Bulleted list'
        className={toolbarButtonProps(state.isBulletList)}
        disabled={disabled || !editor}
        onMouseDown={preventToolbarMouseDown}
        onClick={() => run(() => editor?.chain().focus().toggleBulletList().run())}
      >
        •≡
      </button>
      <button
        type='button'
        title='Numbered list'
        className={toolbarButtonProps(state.isOrderedList)}
        disabled={disabled || !editor}
        onMouseDown={preventToolbarMouseDown}
        onClick={() => run(() => editor?.chain().focus().toggleOrderedList().run())}
      >
        1.≡
      </button>
      <span className='ee-inlinebar__sep' />
      <button
        type='button'
        title='Align left'
        className={toolbarButtonProps(block.align === 'left')}
        disabled={disabled}
        onMouseDown={preventToolbarMouseDown}
        onClick={() => runAlign('left')}
      >
        ⇤
      </button>
      <button
        type='button'
        title='Align center'
        className={toolbarButtonProps(block.align === 'center')}
        disabled={disabled}
        onMouseDown={preventToolbarMouseDown}
        onClick={() => runAlign('center')}
      >
        ⇔
      </button>
      <button
        type='button'
        title='Align right'
        className={toolbarButtonProps(block.align === 'right')}
        disabled={disabled}
        onMouseDown={preventToolbarMouseDown}
        onClick={() => runAlign('right')}
      >
        ⇥
      </button>
    </div>
  );
}
